import { useState, useEffect, useMemo, ReactNode } from "react";
import {
  Group,
  Title,
  Text,
  Stack,
  Badge,
  Box,
  Alert,
  Tabs,
} from "@mantine/core";
import { useResponsive } from "../../../../../context/ResponsiveContext";
import {
  IconPackage,
  IconCalendar,
  IconClock,
  IconUser,
  IconDroplet,
  IconGift,
} from "@tabler/icons-react";
import moment from "moment";
import {
  GetTransactionsResponse,
  TransactionResponse,
  BookedService,
  Product,
  TransactionServicePackage,
  PendingGiftCardLineItem,
} from "../../../../../types/transactions";
import { BusinessInfo } from "../../../../../types/context";
import {
  ClientNote,
  PotentialDuplicateGroup,
} from "../../../../../types/client";
import AppointmentStatusBadgeMenu from "../../../../common/AppointmentStatusBadgeMenu";
import {
  appointmentStatusDisplay,
  statusPickBlockedReason,
} from "../../booking/guards";
import { getSalonTimezone } from "../../../../../utils/timeUtils";
import DuplicateClientWarningIcon from "../../../../common/DuplicateClientWarningIcon";
import {
  calculateDiscountedItemPrice,
  calculateDiscountedUnitPrice,
  getDiscountedLinePrice,
  hasLineDiscount,
} from "../../../../../utils/discountUtils";
import { getLineDiscountLabels } from "../../../../../utils/discountFormatting";
import { formatCurrency } from "../../../../../utils/cashDrawerUtils";
import { notifyError } from "../../../../../utils/notify";
import { isClientPotentialDuplicate } from "../../../../../utils/duplicateGrouping";
import RefundStatusBadge from "../../../../common/RefundStatusBadge";
import {
  isPerUnitBasis,
  perUnitBreakdown,
  unitLabelForBasis,
} from "../../../../../utils/quantityUtils";
import { netCostAfterRedemptionExclTaxTip } from "../../../../../utils/packageRedemptionUtils";
import InlineQuantityStepper from "../../../../common/InlineQuantityStepper";
import DeleteConfirmationModal from "../../../../../components/common/DeleteConfirmationModal";
import ClientLinkButton from "../../../../clients/ClientLinkButton";
import PopupNotesAlert from "../../../../clients/PopupNotesAlert";
import { useClient } from "../../../../../context/ClientContext";
import { useApp } from "../../../../../context/AppContext";
import { ClientServicePackage } from "../../../../../types/packages";
import usePermissions from "../../../../../hooks/usePermissions";
import { SalonContextType } from "../../../../../types/context";
import { useSalon } from "../../../../../context/SalonContext";
import { Employee } from "../../../../../types/salon";
import AppointmentFormsPanel from "./AppointmentFormsPanel";
import ScheduledRemindersPanel from "./ScheduledRemindersPanel";
import AddItemMenu from "./AddItemMenu";
import LineItemRow from "./LineItemRow";
import ProductCommissionLine from "../../../../common/ProductCommissionLine";
import RedemptionBreakdown from "./RedemptionBreakdown";
import SoftNotePanel from "../../../../common/surface/SoftNotePanel";
import EmptyStateCard from "../../../../common/surface/EmptyStateCard";
import ServiceModifiers from "../../../../common/ServiceModifiers/ServiceModifiers";
import surfaceStyles from "../../../../common/surface/surface.module.css";
import styles from "./TransactionDetails.module.css";

interface TransactionDetailsProps {
  transaction: GetTransactionsResponse | TransactionResponse;
  businessInfo: BusinessInfo | null;
  onAddLineItem: (type: "service" | "product" | "package" | "giftcard") => void;
  onEditService: (service: BookedService) => void;
  onEditProduct: (product: Product) => void;
  onDeleteLineItem: (
    lineItem:
      | BookedService
      | Product
      | TransactionServicePackage
      | PendingGiftCardLineItem,
  ) => void;
  onTransactionUpdated: (
    transaction: GetTransactionsResponse | TransactionResponse,
  ) => void;
  /** Persist a status change from the status-badge dropdown. Omit to render the
   *  status as a plain (non-interactive) badge. */
  onStatusUpdate?: (newStatus: string) => void | Promise<void>;
  /** True while a status change is being persisted (shows a spinner). */
  isUpdatingStatus?: boolean;
  errors?: {
    discount?: string;
    general?: string;
  };
  canEdit?: boolean;
  /** Whether the salon has any package/special configured. Gates the "Package
   *  or Special" add action (in the Add menu and the empty-state card) with a
   *  prompt to set one up in Settings when none exist. Defaults to true. */
  hasPackagesConfigured?: boolean;
  /** Gift Cards feature wall — surfaces the "Gift Card" add-item option. Defaults false. */
  giftCardsEnabled?: boolean;
  /** Role permission giftCards.canSellGiftCards — keeps the "Gift Card" add
   *  option; lines already on the ticket render regardless. Defaults true. */
  canSellGiftCards?: boolean;
  /** Only honored when the tab bar is visible (med-spa + at least one service). */
  initialTab?: string | null;
  /** Inline quantity editing, owned by TransactionDetailModal and shared with
   *  the summary. Defaults keep this component renderable in isolation. */
  displayQuantity?: (
    item: BookedService | Product,
    serverQuantity: number,
  ) => number;
  isSaving?: (lineItemId: string) => boolean;
  setQuantity?: (
    variant: "service" | "product",
    item: BookedService | Product,
    next: number,
  ) => void;
}

export default function TransactionDetails({
  transaction,
  businessInfo,
  onAddLineItem,
  onEditService,
  onEditProduct,
  onDeleteLineItem,
  onTransactionUpdated,
  onStatusUpdate,
  isUpdatingStatus = false,
  errors,
  canEdit = true,
  hasPackagesConfigured = true,
  giftCardsEnabled = false,
  canSellGiftCards = true,
  initialTab = null,
  displayQuantity = (_item, serverQuantity) => serverQuantity,
  isSaving = () => false,
  setQuantity = () => {},
}: TransactionDetailsProps) {
  const { isMobile } = useResponsive();
  const { clientsById, potentialDuplicates } = useClient() as {
    clientsById: Record<
      string,
      { id: string; notes?: ClientNote[] } | undefined
    >;
    potentialDuplicates: PotentialDuplicateGroup[];
  };
  const { services: salonServices } = useSalon() as SalonContextType;
  const { employees } = useSalon() as { employees: Employee[] };
  // Items/Forms/Messages tab bar gates on the /access forms permission, not vertical.
  const canViewClientForms =
    usePermissions()?.formsAndWaivers?.canViewClientForms === true;
  const timezone = businessInfo?.timezone || "UTC";
  const [showLineItemDeleteConfirmation, setShowLineItemDeleteConfirmation] =
    useState(false);
  const [lineItemToDelete, setLineItemToDelete] = useState<
    | BookedService
    | Product
    | TransactionServicePackage
    | PendingGiftCardLineItem
    | null
  >(null);
  const [isDeletingLineItem, setIsDeletingLineItem] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>("details");
  const [formsCount, setFormsCount] = useState(0);
  // Tracks forms still owed by client/staff (status not completed/expired).
  // Used by ScheduledRemindersPanel to hide the "Send Form Reminder" button
  // when there is nothing for the client to fill.
  const [pendingFormsCount, setPendingFormsCount] = useState(0);
  const [messagesCount, setMessagesCount] = useState(0);

  const isReadOnly = !canEdit;
  const canAssignProductCommission =
    usePermissions()?.settingsCommissions?.canAssignProductCommission === true;

  const clientPopupNotes = clientsById[transaction.client.id]?.notes;

  // Read-only informational note: does this client own any package that can be
  // redeemed against a service on this transaction? Redemption itself lives in
  // the checkout flow; here we only surface a heads-up note.
  const { getClientServicePackages, editTransactionLineItems } = useApp();
  const [clientPackages, setClientPackages] = useState<ClientServicePackage[]>(
    [],
  );

  // Persist a product line item's commission earner (staff_commission_target)
  // via the shared edit endpoint, then optimistically merge the response —
  // mirroring how quantity/discount edits update the transaction in place.
  const handleCommissionTargetChange = async (
    lineItem: Product,
    staffId: string | null,
  ) => {
    try {
      const response = await editTransactionLineItems({
        transaction_id: transaction.transaction.id,
        line_items: {
          products: [
            {
              id: lineItem.id,
              line_item_id: lineItem.line_item_id,
              staff_commission_target: staffId,
            },
          ],
        },
      });
      if (response?.line_items) {
        onTransactionUpdated({
          ...transaction,
          line_items: response.line_items,
        });
      }
    } catch (error) {
      console.error("Failed to update product commission target:", error);
      notifyError(
        "Couldn't update the commission assignment. Please try again.",
      );
      // Re-throw so the picker keeps its loading state and stays open to retry.
      throw error;
    }
  };
  const clientId = transaction.client?.id;
  const salonId = businessInfo?.id ?? "";
  const isClientDuplicate = isClientPotentialDuplicate(
    potentialDuplicates,
    clientId,
  );

  useEffect(() => {
    let cancelled = false;
    if (!clientId || !salonId) {
      setClientPackages([]);
      return;
    }
    getClientServicePackages(salonId, clientId)
      .then((list) => {
        if (!cancelled) setClientPackages(list);
      })
      .catch((err) => {
        console.error("Error loading client packages for notes:", err);
        if (!cancelled) setClientPackages([]);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, salonId, getClientServicePackages]);

  // Packages covering at least one service on this transaction with balance
  // left. Mirrors the rule in the checkout flow exactly.
  const redeemablePackages = useMemo(() => {
    const txnServiceIds = new Set(
      (transaction.line_items?.booked_services ?? []).map(
        (bs) => bs.salon_service_id,
      ),
    );
    return clientPackages.filter((pkg) =>
      pkg.services.some(
        (svc) =>
          svc.quantity_remaining > 0 && txnServiceIds.has(svc.salon_service_id),
      ),
    );
  }, [clientPackages, transaction.line_items?.booked_services]);

  // Forms & Scheduled Reminders are med-spa-only and only relevant once the
  // appointment has at least one service. When present, the modal body is
  // organized into Items / Forms / Messages tabs; otherwise the detail
  // sections render on their own (no tab bar).
  const showTabs =
    canViewClientForms &&
    (transaction.line_items?.booked_services?.length ?? 0) > 0;

  // Reset to the Items tab whenever the transaction changes (e.g. the rebook
  // flow swaps `transaction` in place without unmounting) or `showTabs` flips.
  // Otherwise a lingering "forms"/"messages" activeTab can leave the body blank
  // when the new transaction has no Forms/Messages tabs in the tree.
  useEffect(() => {
    if (initialTab && showTabs) {
      setActiveTab(initialTab);
    } else {
      setActiveTab("details");
    }
  }, [transaction.transaction.id, showTabs, initialTab]);

  // Helper function to format amounts - shows "-" for zero values
  const formatAmount = (amount: number): string => {
    return amount === 0 ? "-" : formatCurrency(amount);
  };

  function formatTransactionDate(
    txn: GetTransactionsResponse | TransactionResponse,
    tz: string,
  ): string {
    // Service-less sales (e.g. gift-card-only or product-only Quick Sales) have
    // no appointment, so fall back to when the transaction itself was created.
    const startDt =
      txn.line_items.booked_services[0]?.start_dt ?? txn.transaction.created_dt;
    if (!startDt) return "Unknown date";

    return isMobile
      ? moment.utc(startDt).tz(tz).format("M/D/YY")
      : moment.utc(startDt).tz(tz).format("MMM DD, YYYY");
  }

  function formatTransactionTime(
    txn: GetTransactionsResponse | TransactionResponse,
    tz: string,
  ): string {
    const firstService = txn.line_items.booked_services[0];
    // Service-less sales have no start/end range; show the created time instead.
    if (!firstService) {
      const createdDt = txn.transaction.created_dt;
      return createdDt
        ? moment.utc(createdDt).tz(tz).format("h:mm A")
        : "Unknown time";
    }

    const startTime = moment.utc(firstService.start_dt).tz(tz).format("h:mm A");
    const endTime = moment
      .utc(firstService.calc__end_dt)
      .tz(tz)
      .format("h:mm A");

    return `${startTime} - ${endTime}`;
  }

  // Shown on the disabled decrement control once a line item hits its minimum
  // quantity — removal is a separate, explicit action.
  const REMOVE_TOOLTIP =
    "In order to remove this from your transaction, please delete it.";

  const basisForService = (service: BookedService) =>
    (salonServices ?? []).find((svc) => svc.id === service.salon_service_id)
      ?.price_basis;

  const handleDeleteLineItem = async () => {
    if (!lineItemToDelete) return;

    try {
      setIsDeletingLineItem(true);
      await onDeleteLineItem(lineItemToDelete);
      setShowLineItemDeleteConfirmation(false);
      setLineItemToDelete(null);
      setIsDeletingLineItem(false);
    } catch (error) {
      console.error("Failed to delete line item:", error);
      setIsDeletingLineItem(false);
    }
  };

  const services = transaction.line_items.booked_services;
  const products = transaction.line_items.products;
  const servicePackages = transaction.line_items.service_packages ?? [];
  const giftCards = transaction.line_items.gift_cards ?? [];

  return (
    <Stack gap="lg">
      {/* Error Display */}
      {(errors?.discount || errors?.general) && (
        <Stack gap="sm">
          {errors.discount && (
            <Alert color="red" title="Discount Error" withCloseButton>
              {errors.discount}
            </Alert>
          )}
          {errors.general && (
            <Alert color="red" title="Error" withCloseButton>
              {errors.general}
            </Alert>
          )}
        </Stack>
      )}

      {/* Header */}
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start" gap="sm" wrap="wrap">
          <Title order={2} className={styles.headerTitle}>
            Appointment Details for {transaction.client.first_name}{" "}
            {transaction.client.last_name}
          </Title>
          <Group gap="xs" wrap="nowrap">
            <AppointmentStatusBadgeMenu
              currentStatus={transaction.transaction.appointment_status}
              display={appointmentStatusDisplay(
                transaction.transaction.appointment_status,
                transaction,
                getSalonTimezone(businessInfo),
              )}
              onChange={(next) => onStatusUpdate?.(next)}
              getStatusBlockedReason={(next) =>
                statusPickBlockedReason(
                  next,
                  transaction,
                  getSalonTimezone(businessInfo),
                )
              }
              disabled={!canEdit || !onStatusUpdate}
              loading={isUpdatingStatus}
              size="lg"
              radius="xl"
              className={styles.statusBadge}
              withDot
            />
            <RefundStatusBadge
              refundStatus={transaction.transaction.refund_status}
            />
          </Group>
        </Group>

        {/* Date / time / client meta row */}
        <Box className={styles.metaRow}>
          {!isMobile && (
            <Box component="span" className={styles.metaItem}>
              <IconCalendar size={15} />
              {formatTransactionDate(transaction, timezone)}
            </Box>
          )}
          <Box component="span" className={styles.metaItem}>
            <IconClock size={15} />
            {isMobile && `${formatTransactionDate(transaction, timezone)} `}
            {formatTransactionTime(transaction, timezone)}
          </Box>
          {isClientDuplicate ? (
            <Group gap={6} wrap="nowrap" align="center">
              <ClientLinkButton client={transaction.client} />
              <DuplicateClientWarningIcon size={16} />
            </Group>
          ) : (
            <ClientLinkButton client={transaction.client} />
          )}
        </Box>

        {/* Notes — popup client notes + applicable-package heads-up. Display
            only (no editing from here). */}
        <PopupNotesAlert variant="soft" notes={clientPopupNotes} />
        {redeemablePackages.length > 0 && (
          <SoftNotePanel
            icon={<IconPackage size={15} />}
            iconColor="var(--brand-teal)"
          >
            Client has {redeemablePackages.length} applicable package
            {redeemablePackages.length === 1 ? "" : "s"} that can be redeemed at
            checkout: {redeemablePackages.map((pkg) => pkg.name).join(", ")}
          </SoftNotePanel>
        )}
        {/* "Client has rewards available at checkout: …" SoftNotePanel
            removed 2026-08-26 per feedback — the same information is in the
            Rewards & Promotions panel's `Browse Available Rewards (N)`
            button label, and repeating it as a soft-note above the tabs was
            visual noise. */}
      </Stack>

      <Tabs
        value={activeTab}
        onChange={setActiveTab}
        classNames={{ list: styles.tabsList, tab: styles.tab }}
      >
        {showTabs && (
          <Tabs.List>
            <Tabs.Tab value="details">Transaction Items</Tabs.Tab>
            <Tabs.Tab
              value="forms"
              rightSection={
                formsCount > 0 ? (
                  <Badge
                    size="sm"
                    circle
                    variant={activeTab === "forms" ? "filled" : "light"}
                    color={activeTab === "forms" ? "var(--brand-teal)" : "gray"}
                  >
                    {formsCount}
                  </Badge>
                ) : undefined
              }
            >
              Forms
            </Tabs.Tab>
            <Tabs.Tab
              value="messages"
              rightSection={
                messagesCount > 0 ? (
                  <Badge
                    size="sm"
                    circle
                    variant={activeTab === "messages" ? "filled" : "light"}
                    color={
                      activeTab === "messages" ? "var(--brand-teal)" : "gray"
                    }
                  >
                    {messagesCount}
                  </Badge>
                ) : undefined
              }
            >
              Messages
            </Tabs.Tab>
          </Tabs.List>
        )}

        <Tabs.Panel value="details" pt={showTabs ? "lg" : 0}>
          <Stack gap="xl">
            {/* Transaction Items header with the single Add menu */}
            <Group justify="space-between" align="center">
              <Title order={3} className={styles.sectionHeading}>
                Transaction Items
              </Title>
              {!isReadOnly && (
                <AddItemMenu
                  onAddLineItem={onAddLineItem}
                  hasPackagesConfigured={hasPackagesConfigured}
                  giftCardsEnabled={giftCardsEnabled && canSellGiftCards}
                />
              )}
            </Group>

            {/* Services group */}
            <Box>
              <Box className={surfaceStyles.eyebrow} mb={6}>
                Service
              </Box>
              {services.length > 0 ? (
                <Box>
                  {services.map((service) => {
                    const basis = basisForService(service);
                    const isPerUnit = isPerUnitBasis(basis);
                    const hasDiscount = hasLineDiscount(service);
                    const currentQty = displayQuantity(
                      service,
                      service.quantity ?? 1,
                    );

                    // Per-unit/syringe services show the unit rate on the row
                    // (the extended total lives in the Appointment Summary);
                    // flat per_service services keep the full line total.
                    let originalPrice: string | undefined;
                    let price: string;
                    let priceSuffix: ReactNode;
                    if (isPerUnit) {
                      // Fixed amounts are line-level, so they're spread across
                      // the units (a $50 line discount on 24 units is ~$2.08
                      // off each unit, not $50 off one unit); percentages are
                      // scale-invariant and apply to the rate directly.
                      const discountedUnit = hasDiscount
                        ? calculateDiscountedUnitPrice(
                            service,
                            service.unit_price,
                            currentQty,
                          )
                        : service.unit_price;
                      originalPrice = hasDiscount
                        ? formatAmount(service.unit_price)
                        : undefined;
                      price = formatAmount(discountedUnit);
                      priceSuffix = `/${unitLabelForBasis(basis, 1)}`;
                    } else if ((service.calc__units_redeemed ?? 0) > 0) {
                      // Redeemed per-service line: strike the full cost and
                      // show the net after redemption (and discount), excl
                      // tax/tip. Struck even without a discount.
                      originalPrice = formatAmount(
                        service.calc__cost_excl_tips_discounts,
                      );
                      price = formatAmount(
                        netCostAfterRedemptionExclTaxTip(service),
                      );
                    } else {
                      originalPrice = hasDiscount
                        ? formatAmount(service.calc__cost_excl_tips_discounts)
                        : undefined;
                      price = hasDiscount
                        ? formatAmount(calculateDiscountedItemPrice(service))
                        : formatAmount(service.calc__cost_excl_tips_discounts);
                    }

                    // Editable per-unit rows get an inline stepper; read-only
                    // ones show the static "2.5 units × $12.00" breakdown.
                    const quantityControl =
                      isPerUnit && !isReadOnly ? (
                        <InlineQuantityStepper
                          value={currentQty}
                          onChange={(v) => setQuantity("service", service, v)}
                          min={0.01}
                          step={basis === "per_syringe" ? 0.5 : 1}
                          decimalScale={2}
                          unitLabel={unitLabelForBasis(basis, currentQty)}
                          minReachedTooltip={REMOVE_TOOLTIP}
                          saving={isSaving(service.line_item_id)}
                          ariaLabel={`${service.name} quantity`}
                        />
                      ) : undefined;
                    const readOnlyBreakdown =
                      isPerUnit && isReadOnly
                        ? perUnitBreakdown(
                            currentQty,
                            basis,
                            service.unit_price,
                          )
                        : null;

                    return (
                      <LineItemRow
                        key={service.id}
                        variant="service"
                        name={service.name}
                        meta={
                          <>
                            <Box component="span" className={styles.metaItem}>
                              <IconClock size={15} />
                              {moment
                                .utc(service.start_dt)
                                .tz(timezone)
                                .format("h:mm A")}{" "}
                              –{" "}
                              {moment
                                .utc(service.calc__end_dt)
                                .tz(timezone)
                                .format("h:mm A")}
                            </Box>
                            <Box component="span" className={styles.metaItem}>
                              <IconUser size={15} />
                              {service.staff_name}
                            </Box>
                            {readOnlyBreakdown && (
                              <Box component="span" className={styles.metaItem}>
                                {readOnlyBreakdown}
                              </Box>
                            )}
                          </>
                        }
                        quantityControl={quantityControl}
                        discountChips={getLineDiscountLabels(service)}
                        originalPrice={originalPrice}
                        price={price}
                        priceSuffix={priceSuffix}
                        onEdit={() => onEditService(service)}
                        onDelete={() => {
                          setLineItemToDelete(service);
                          setShowLineItemDeleteConfirmation(true);
                        }}
                        deleteDisabled={services.length <= 1}
                        deleteDisabledTooltip="Each transaction must have at least one service.  If you want to cancel this service, please click 'Cancel Booking' instead."
                        readOnly={isReadOnly}
                      >
                        <ServiceModifiers
                          subLineItems={service.sub_line_items}
                          serviceName={service.name}
                        />
                        <RedemptionBreakdown
                          service={service}
                          priceBasis={basis}
                          variant="detail"
                        />
                      </LineItemRow>
                    );
                  })}
                </Box>
              ) : (
                <EmptyStateCard
                  icon={<IconDroplet size={20} stroke={1.9} />}
                  title="No services added"
                  message="Add a service to start building this appointment."
                  actionLabel={!isReadOnly ? "Add Service" : undefined}
                  onAction={
                    !isReadOnly ? () => onAddLineItem("service") : undefined
                  }
                />
              )}
            </Box>

            {/* Products group */}
            <Box>
              <Box className={surfaceStyles.eyebrow} mb={6}>
                Products
              </Box>
              {products.length > 0 ? (
                <Box>
                  {products.map((product) => {
                    const hasDiscount = hasLineDiscount(product);
                    const currentQty = displayQuantity(
                      product,
                      product.quantity,
                    );
                    const lineTotal = product.unit_price * currentQty;

                    return (
                      <LineItemRow
                        key={product.id}
                        variant="product"
                        imageUrl={product.img_url || undefined}
                        name={product.name}
                        meta={
                          isReadOnly ? (
                            <Box component="span" className={styles.metaItem}>
                              <IconPackage size={15} />
                              Qty {currentQty}
                            </Box>
                          ) : undefined
                        }
                        quantityControl={
                          !isReadOnly ? (
                            <InlineQuantityStepper
                              value={currentQty}
                              onChange={(v) =>
                                setQuantity("product", product, v)
                              }
                              min={1}
                              step={1}
                              decimalScale={0}
                              minReachedTooltip={REMOVE_TOOLTIP}
                              saving={isSaving(product.line_item_id)}
                              ariaLabel={`${product.name} quantity`}
                            />
                          ) : undefined
                        }
                        discountChips={getLineDiscountLabels(product)}
                        originalPrice={
                          hasDiscount ? formatAmount(lineTotal) : undefined
                        }
                        price={
                          hasDiscount
                            ? formatAmount(
                                calculateDiscountedItemPrice(
                                  product,
                                  currentQty,
                                ),
                              )
                            : formatAmount(lineTotal)
                        }
                        onEdit={() => onEditProduct(product)}
                        onDelete={() => {
                          setLineItemToDelete(product);
                          setShowLineItemDeleteConfirmation(true);
                        }}
                        readOnly={isReadOnly}
                      >
                        <ProductCommissionLine
                          staffCommissionTarget={
                            product.staff_commission_target
                          }
                          employees={employees}
                          canEdit={canAssignProductCommission && !isReadOnly}
                          onSave={(staffId) =>
                            handleCommissionTargetChange(product, staffId)
                          }
                        />
                      </LineItemRow>
                    );
                  })}
                </Box>
              ) : (
                <EmptyStateCard
                  icon={<IconPackage size={20} stroke={1.9} />}
                  title="No products added"
                  message="Sell a retail product alongside this service."
                  actionLabel={!isReadOnly ? "Add Product" : undefined}
                  onAction={
                    !isReadOnly ? () => onAddLineItem("product") : undefined
                  }
                />
              )}
            </Box>

            {/* Packages & Specials group — always shown (like Services /
                Products) so it reads as a first-class item type; the empty
                state prompts selling one via the Add menu → Package or Special. */}
            <Box>
              <Box className={surfaceStyles.eyebrow} mb={6}>
                Packages & Specials
              </Box>
              {servicePackages.length > 0 ? (
                <Box>
                  {servicePackages.map((pkg) => {
                    // A package's base is its unit_price (the bundle price).
                    const hasDiscount = hasLineDiscount(pkg);
                    const discountedPrice = hasDiscount
                      ? getDiscountedLinePrice(pkg, pkg.unit_price)
                      : pkg.unit_price;

                    return (
                      <LineItemRow
                        key={pkg.line_item_id}
                        variant="package"
                        name={pkg.name}
                        meta={
                          <Box component="span" className={styles.metaItem}>
                            <IconPackage size={15} />
                            {pkg.services && pkg.services.length > 0
                              ? "Package (granted)"
                              : "Package"}
                          </Box>
                        }
                        discountChips={getLineDiscountLabels(pkg)}
                        originalPrice={
                          hasDiscount ? formatAmount(pkg.unit_price) : undefined
                        }
                        price={formatAmount(discountedPrice)}
                        onDelete={() => {
                          setLineItemToDelete(pkg);
                          setShowLineItemDeleteConfirmation(true);
                        }}
                        readOnly={isReadOnly}
                      />
                    );
                  })}
                </Box>
              ) : (
                <EmptyStateCard
                  icon={<IconPackage size={20} stroke={1.9} />}
                  title="No packages or specials added"
                  message="Sell a package or special on this appointment."
                  actionLabel={
                    !isReadOnly && hasPackagesConfigured
                      ? "Add Package or Special"
                      : undefined
                  }
                  onAction={
                    !isReadOnly && hasPackagesConfigured
                      ? () => onAddLineItem("package")
                      : undefined
                  }
                />
              )}
            </Box>

            {/* Gift Cards group — feature-walled and shown only once a gift card
                is on the ticket (added via the Add menu → Gift Card). Removable
                like any other line while the sale is unpaid. */}
            {giftCardsEnabled && giftCards.length > 0 && (
              <Box>
                <Box className={surfaceStyles.eyebrow} mb={6}>
                  Gift Cards
                </Box>
                <Box>
                  {giftCards.map((giftCard) => (
                    <LineItemRow
                      key={giftCard.line_item_id}
                      variant="package"
                      name={giftCard.name}
                      meta={
                        <Box component="span" className={styles.metaItem}>
                          <IconGift size={15} />
                          {giftCard.code
                            ? `Gift card · ${giftCard.code}`
                            : "Gift card"}
                        </Box>
                      }
                      price={formatAmount(giftCard.amount)}
                      onDelete={() => {
                        setLineItemToDelete(giftCard);
                        setShowLineItemDeleteConfirmation(true);
                      }}
                      readOnly={isReadOnly}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Stack>
        </Tabs.Panel>

        {showTabs && (
          <Tabs.Panel value="forms" pt="md" keepMounted>
            <AppointmentFormsPanel
              transactionUuid={transaction.transaction.id}
              salonUuid={businessInfo?.id}
              clientUuid={transaction.client?.id}
              canEdit={canEdit}
              initialForms={transaction.forms}
              onCountChange={setFormsCount}
              onPendingCountChange={setPendingFormsCount}
              onFormsChange={(forms) =>
                onTransactionUpdated({ ...transaction, forms })
              }
            />
          </Tabs.Panel>
        )}

        {showTabs && (
          <Tabs.Panel value="messages" pt="md" keepMounted>
            <ScheduledRemindersPanel
              transactionUuid={transaction.transaction.id}
              timezone={timezone}
              canEdit={canEdit}
              onCountChange={setMessagesCount}
              hasPendingForms={pendingFormsCount > 0}
              appointmentStatus={transaction.transaction.appointment_status}
            />
          </Tabs.Panel>
        )}
      </Tabs>

      {/* Service Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showLineItemDeleteConfirmation}
        onClose={() => setShowLineItemDeleteConfirmation(false)}
        onConfirm={handleDeleteLineItem}
        title="Delete Service"
        message={`Are you sure you want to delete ${lineItemToDelete?.name} from this transaction? This action cannot be undone.`}
        isLoading={isDeletingLineItem}
      />
    </Stack>
  );
}
